import { Ref, useCallback, useRef } from 'react';

import clsx from 'clsx';
import { PillButtonProps } from 'ui-kit/Buttons';
import { ComboDropdownValuePill } from 'ui-kit/Combobox/ComboDropdownValuePill';
import Select, { Props, SelectInstance, InputProps, OptionProps, components } from 'react-select';

export type PillDropdownProps<T> = Pick<
  Props<T, false>,
  'getOptionLabel' | 'getOptionValue' | 'options' | 'value' | 'onChange'
> & {
  iconOnClick?: PillButtonProps['iconOnClick'];
  iconName?: PillButtonProps['iconName'];
  description: PillButtonProps['description'];
  className?: string;
  controlStyles?: React.CSSProperties;
};

export function PillDropdown<T>(props: PillDropdownProps<T>) {
  const ref = useRef<SelectInstance<T, false>>(null);
  const { className, ...rest } = props;
  const ControlComp = useCallback(
    (controlProps: InputProps<T, false>) => {
      const label = ref.current?.getOptionLabel(controlProps.getValue()[0] || ({} as T));

      // Manually map event handlers to match HTMLButtonElement types
      const inputProps = {
        onClick: controlProps.onClick as React.MouseEventHandler<HTMLButtonElement>,
        onMouseDown: controlProps.onMouseDown as React.MouseEventHandler<HTMLButtonElement>,
        onKeyDown: controlProps.onKeyDown as React.KeyboardEventHandler<HTMLButtonElement>,
        onFocus: controlProps.onFocus as React.FocusEventHandler<HTMLButtonElement>,
        onBlur: controlProps.onBlur as React.FocusEventHandler<HTMLButtonElement>,
        'aria-label': controlProps['aria-label'],
        'aria-labelledby': controlProps['aria-labelledby'],
        'aria-describedby': controlProps['aria-describedby'],
        'aria-invalid': controlProps['aria-invalid'],
        'aria-errormessage': controlProps['aria-errormessage'],
        tabIndex: controlProps.tabIndex,
        role: controlProps.role,
      };

      return (
        <ComboDropdownValuePill
          // PillButton hardcodes mr-[18px] (a legacy spacing value, from
          // before pill rows switched to flex gap). Every other filter pill
          // is a direct child of PillFilters' row, whose [&>*]:m-0 zeroes
          // that out — but this pill is nested one level deeper, inside
          // this component's own react-select wrapper, so [&>*]:m-0 (which
          // only reaches direct children) never touches it: the boolean
          // filter pill carried 18px of extra trailing margin no sibling
          // pill has. !mr-0 needs !important — mr-[18px] and mr-0 are
          // equal-specificity utility classes, so without it the winner
          // depends on Tailwind's generated stylesheet order, not the
          // className string's order.
          className={clsx(className, '!mr-0')}
          ref={controlProps.innerRef as Ref<HTMLButtonElement>}
          {...inputProps}
          description={props.description}
          value={label}
          iconName={props.iconName}
          iconOnClick={props.iconOnClick}
          isActive={controlProps.selectProps.menuIsOpen}
        />
      );
    },
    [props.description, props.iconName, props.iconOnClick],
  );

  // react-select 5.7.3 emits no role on its options -- its Option innerProps
  // are only id/onClick/onMouseMove/onMouseOver/tabIndex -- so the menu was a
  // bag of anonymous divs that no assistive tech (and no role-based locator)
  // could find. Inject the role through innerProps rather than replacing the
  // option body, so react-select's own default Option keeps rendering and the
  // classNames.option styling above still applies. Same hand-rolled role as
  // its sibling PillButtonCombobox's OptionComp.
  const OptionComp = useCallback(
    (optionProps: OptionProps<T, false>) => (
      <components.Option
        {...optionProps}
        innerProps={{
          ...optionProps.innerProps,
          role: 'option',
          'aria-selected': optionProps.isSelected,
        }}
      />
    ),
    [],
  );

  return (
    <Select
      ref={ref}
      {...rest}
      // combobox is not a name-from-content role, so the pill's visible
      // description text ("CRA") is not its accessible name -- without this the
      // pill button react-select's role lands on (see ControlComp above) has no
      // accessible name at all. react-select forwards this straight through to
      // the Input component's aria-label, which ControlComp already puts on the
      // pill.
      aria-label={props.description}
      components={{
        Input: ControlComp,
        Option: OptionComp,
        SingleValue: () => null,
        Placeholder: () => null,
        IndicatorsContainer: () => null,
      }}
      // classNames (Tailwind) for color/typography, styles (inline CSS) for
      // anything layout-numeric: the last styled-components/useTheme
      // consumer left in ui-kit, following the same
      // PillButtonCombobox/DropdownPillCombobox* pattern for color, but
      // react-select's own base menu style (position/width/zIndex) is
      // itself applied as an inline `style` attribute even under
      // unstyled={true} -- an inline style always beats a class on
      // specificity, so a Tailwind z-10/min-w-full/w-max class here would
      // silently never win against it. minWidth/width/zIndex have to go
      // through styles.menu instead, where they reliably override.
      classNames={{
        menu: () =>
          clsx(
            'rounded border border-gray-700 bg-gray-950 shadow-[4px_4px_8px_rgba(0,0,0,0.5)]',
            // font-heebo text-xs leading-[1.67] font-normal: Tailwind
            // equivalent of the old theme.typography.primaryXSmall shorthand
            // ($normalWeight 12px/1.67 $primaryFontFamily). text-xs
            // leading-[1.67] matches PillButtonCombobox's own labels (same
            // scss size/line-height); font-heebo is added here too since
            // primaryXSmall's shorthand is explicit about the family and
            // there's no ambient body-level Heebo rule to inherit it from.
            'font-heebo text-xs font-normal leading-[1.67] text-white',
          ),
        // Was theme.color.black (#171717) / accentEmphasis (pink) before
        // the port to ui-kit -- a slightly-off-shade black and a full pink
        // border, matching neither PillButtonCombobox's flyout nor any
        // pill's own surface. gray-950/gray-700 (menu, above) are the exact
        // Tailwind tokens those theme values resolved to by hex.
        option: (optionProps) =>
          clsx(
            'px-2 py-1 first:rounded-t last:rounded-b',
            // theme.color.slate300 (#3B4351) is Tailwind's slate-500, not
            // slate-300 -- the scss slate scale is offset two steps from
            // Tailwind's, confirmed by hex not by matching the number.
            (optionProps.isSelected || optionProps.isFocused) && 'bg-slate-500',
          ),
      }}
      styles={{
        menu: (css) => ({
          ...css,
          // Absolutely positioned against the SelectContainer (the nearest
          // positioned ancestor), so a percentage minWidth floors the menu
          // at the rendered pill's own width -- same as usePopout's
          // matchReferenceWidth does for the other filter types' dropdowns
          // -- but width: max-content grows past it for an option label
          // that needs more room.
          minWidth: '100%',
          width: 'max-content',
          // Every sibling flyout in ui-kit/Combobox
          // (DropdownPillComboboxMultiValue/SingleAdd/SingleValue,
          // DropdownPillListInput) sits at z-40 -- above Pill's own
          // remove-badge (z-30, Pill.tsx), below the app's modal/dialog
          // tier (z-50). react-select's own unstyled base is zIndex: 1,
          // which would put this menu under the next filter row's remove
          // badge once pills wrap.
          zIndex: 40,
        }),
        control: (css) => ({
          ...css,
          // react-select's own default is minHeight: 38 — taller than every
          // other pill (PillButton's h-[30px]), which used to be masked by
          // BooleanPillFilter passing controlStyles={{ minHeight: '32px' }}
          // explicitly. That got dropped as a special case in the pill
          // style unification, exposing the 38px default: the boolean
          // filter pill rendered visibly taller than its siblings. Default
          // to matching PillButton's height here instead, so every
          // PillDropdown consumer gets it for free; controlStyles can still
          // override it for a caller that genuinely needs something else.
          minHeight: '30px',
          ...(props?.controlStyles || {}),
        }),
      }}
      closeMenuOnSelect={true}
      unstyled={true}
      openMenuOnClick={true}
    />
  );
}
